import { useState, useEffect } from 'react';
import { createProperty, updateProperty } from '../../services/propertyService';
import { searchClients } from '../../services/clientService';
import type { Client } from '../../types/client';
import type { Property } from '../../types/property';

interface AddPropertyModalProps {
  onClose: () => void;
  onSuccess: () => void;
  clientId?: string;
  property?: Property;
}

export function AddPropertyModal({ onClose, onSuccess, clientId, property }: AddPropertyModalProps) {
  const isEdit = !!property;
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(property?.clientId || clientId || '');
  const [name, setName] = useState(property?.name || '');
  const [address, setAddress] = useState(property?.address || '');
  const [toNumber, setToNumber] = useState(property?.toNumber || '');
  const [websiteUrl, setWebsiteUrl] = useState(property?.websiteUrl || '');
  const [timezone, setTimezone] = useState(property?.timezone || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    searchClients({}, { pageSize: 1000, orderBy: 'name', order: 'asc' }).then(setClients);
  }, []);

  const e164Regex = /^\+1[2-9]\d{9}$/;
  const toNumberInvalid = toNumber.trim().length > 0 && !e164Regex.test(toNumber.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId || !name.trim() || !address.trim() || !toNumber.trim() || toNumberInvalid) return;

    setError('');
    setLoading(true);
    try {
      if (isEdit) {
        await updateProperty(property.id, {
          ...property,
          name: name.trim(),
          address: address.trim(),
          toNumber: toNumber.trim() || undefined,
          websiteUrl: websiteUrl.trim() || undefined,
          timezone: timezone || undefined,
        });
      } else {
        await createProperty({
          clientId: selectedClientId,
          name: name.trim(),
          address: address.trim(),
          toNumber: toNumber.trim() || undefined,
          websiteUrl: websiteUrl.trim() || undefined,
          timezone: timezone || undefined,
        });
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update property' : 'Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{isEdit ? 'Edit Property' : 'Add Property'}</h2>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Client *</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  disabled={!!clientId || isEdit}
                  required
                  className={`${inputClass} ${clientId || isEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select a client</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="Property name"
                />
              </div>
              <div>
                <label className={labelClass}>Address *</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                  className={inputClass}
                  placeholder="Property address"
                />
              </div>
              <div>
                <label className={labelClass}>To Number *</label>
                <input
                  type="tel"
                  value={toNumber}
                  onChange={(e) => setToNumber(e.target.value)}
                  required
                  className={`${inputClass}${toNumberInvalid ? ' border-red-400' : ''}`}
                  placeholder="+13031231234"
                />
                {toNumberInvalid && (
                  <p className="text-red-500 text-xs mt-1">Must be E.164 format (e.g. +13031231234)</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Web Site URL</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  className={inputClass}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className={labelClass}>Timezone</label>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={inputClass}>
                  <option value="">Select timezone</option>
                  <option value="Pacific/Honolulu">Hawaii</option>
                  <option value="America/Anchorage">Alaska</option>
                  <option value="America/Los_Angeles">Pacific</option>
                  <option value="America/Denver">Mountain</option>
                  <option value="America/Chicago">Central</option>
                  <option value="America/New_York">Eastern</option>
                </select>
              </div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedClientId || !name.trim() || !address.trim() || !toNumber.trim() || toNumberInvalid}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save' : 'Add Property')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
